import { useCallback } from 'react';

import { useAssignLeadsToUserMutation } from '@app/api/Leads';
import type { ModelLead } from '@app/api/Leads/types';
import {
  FRONT_WARNING,
  RIGHTS_IS_OWNER_CHANGE,
  RIGHTS_LEADS,
  SUCCESS,
  WARNING,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

export interface LeadResponsibleChangeSuccessParams {
  responsibleUserId: number;
  updatedLead: ModelLead | null;
}

interface UseLeadResponsibleChangeParams {
  leadId: number | null;
  onSuccess?(params: LeadResponsibleChangeSuccessParams): void | Promise<void>;
}

export const useLeadResponsibleChange = ({
  leadId,
  onSuccess,
}: UseLeadResponsibleChangeParams) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { userAccessRights } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const [assignLeadsToUser, { isLoading }] = useAssignLeadsToUserMutation();

  const hasRightToChangeResponsible =
    userAccessRights?.[RIGHTS_LEADS]?.[RIGHTS_IS_OWNER_CHANGE] === true;

  const changeResponsible = useCallback(
    async (responsibleUserId: string | number) => {
      const nextResponsibleUserId =
        typeof responsibleUserId === 'number'
          ? responsibleUserId
          : Number(responsibleUserId);

      if (!Number.isFinite(nextResponsibleUserId)) {
        showNotification('Информация по исполнителю не найдена', FRONT_WARNING);
        return;
      }

      if (!leadId) {
        showNotification('Лид не был распознан', FRONT_WARNING);
        return;
      }

      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        const result = await assignLeadsToUser({
          ActiveBranchId,
          forUserId: nextResponsibleUserId,
          data: [leadId],
        }).unwrap();
        const updatedLead = result[0] ?? null;

        showNotification('Ответственный изменен успешно', SUCCESS);
        await onSuccess?.({
          responsibleUserId: nextResponsibleUserId,
          updatedLead,
        });
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [ActiveBranchId, assignLeadsToUser, leadId, onSuccess, showNotification]
  );

  return {
    changeResponsible,
    hasRightToChangeResponsible,
    isResponsibleChanging: isLoading,
  };
};
